import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Minus, Plus, ShoppingBag, Trash2, X, LocateFixed, Search, Loader2 } from "lucide-react";
import { WHATSAPP_NUMBER, buildOrderMessage, calcDeliveryFee, haversineKm, STORE_COORDS, SPOON_PRICE, DELIVERY_KM_OPTIONS, formatBRL } from "../data/menu";
import { OrderConfirmation } from "./OrderConfirmation";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENTS = ["Pix", "Cartão na entrega", "Dinheiro"];

const inputCls =
  "w-full rounded-xl border border-purple-500/25 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-purple-300/40 outline-none transition-colors duration-200 focus:border-emerald-400";

export const CartDock = ({
  cart,
  total,
  count,
  open,
  setOpen,
  onInc,
  onDec,
  onRemove,
  onClear,
  onNavigate,
}) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [placedOrder, setPlacedOrder] = useState(null);
  const [mode, setMode] = useState("entrega");
  const [address, setAddress] = useState("");
  const [payment, setPayment] = useState("Pix");
  const [notes, setNotes] = useState("");
  const [distance, setDistance] = useState(null);
  const [locating, setLocating] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const suggestTimer = useRef(null);

  const onAddressChange = (e) => {
    const v = e.target.value;
    setAddress(v);
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    if (v.trim().length < 4) {
      setSuggestions([]);
      return;
    }
    suggestTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/places/autocomplete?q=${encodeURIComponent(v.trim())}`);
        if (!r.ok) return;
        const data = await r.json();
        setSuggestions(data.suggestions || []);
      } catch {}
    }, 400);
  };

  const pickSuggestion = async (s) => {
    setAddress(s.text);
    setSuggestions([]);
    setLocating(true);
    try {
      const r = await fetch(`${API}/places/${s.place_id}`);
      if (!r.ok) throw new Error();
      const data = await r.json();
      applyCoords(data.lat, data.lng);
    } catch {
      toast.error("Não consegui localizar esse endereço");
    } finally {
      setLocating(false);
    }
  };

  const applyCoords = (lat, lng) => {
    const km = haversineKm(STORE_COORDS.lat, STORE_COORDS.lng, lat, lng);
    setDistance(Math.max(0.1, Math.round(km * 10) / 10));
    toast.success(`Distância calculada: ≈ ${km.toFixed(1).replace(".", ",")} km da loja`);
  };

  const locate = () => {
    if (!navigator.geolocation) {
      setShowManual(true);
      toast.error("Seu navegador não suporta localização");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        applyCoords(pos.coords.latitude, pos.coords.longitude);
        setLocating(false);
      },
      () => {
        setLocating(false);
        setShowManual(true);
        toast.error("Não consegui acessar sua localização. Informe a distância manualmente.");
      },
      { timeout: 10000 }
    );
  };

  const calcByAddress = async () => {
    if (!address.trim()) {
      toast.error("Digite o endereço primeiro");
      return;
    }
    setLocating(true);
    try {
      const r = await fetch(`${API}/geocode?q=${encodeURIComponent(address)}`);
      if (!r.ok) throw new Error();
      const data = await r.json();
      applyCoords(data.lat, data.lng);
    } catch {
      setShowManual(true);
      toast.error("Endereço não encontrado. Informe a distância manualmente.");
    } finally {
      setLocating(false);
    }
  };
  const { user } = useAuth();

  useEffect(() => {
    if (open && user) {
      if (!name && user.name) setName(user.name);
      if (!address && user.address) setAddress(user.address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const fee = mode === "entrega" && distance != null ? calcDeliveryFee(total, distance) : 0;
  const [spoonChoice, setSpoonChoice] = useState(null);
  const [spoonQty, setSpoonQty] = useState(1);
  const spoonsCost = spoonChoice === "sim" ? spoonQty * SPOON_PRICE : 0;
  const grandTotal = total + fee + spoonsCost;

  const send = async () => {
    if (!cart.length) return;
    if (!name.trim()) {
      toast.error("Informe seu nome para enviar o pedido");
      return;
    }
    const digits = phone.replace(/\D/g, "");
    if (!/^\d{10,11}$/.test(digits)) {
      toast.error("Informe seu WhatsApp com DDD (ex: 41 99999-9999)");
      return;
    }
    if (mode === "entrega" && !address.trim()) {
      toast.error("Informe o endereço para entrega");
      return;
    }
    if (mode === "entrega" && distance == null) {
      toast.error("Selecione a distância aproximada para calcular a entrega");
      return;
    }
    if (spoonChoice == null) {
      toast.error("Informe se deseja colher");
      return;
    }
    const spoons = spoonChoice === "sim" ? spoonQty : 0;
    const msg = buildOrderMessage(
      cart,
      { name, phone: digits, mode, address, payment, notes, fee, distanceKm: distance, spoons },
      total
    );
    let newOrderId = null;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
    try {
      const r = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          customer_name: name.trim(),
          phone: digits,
          mode,
          address,
          payment,
          notes,
          distance_km: distance,
          spoons,
          items: cart.map((i) => ({
            title: i.title,
            details: i.details,
            unitPrice: i.unitPrice,
            qty: i.qty,
          })),
          subtotal: total,
          fee,
          total: grandTotal,
        }),
      });
      if (r.ok) newOrderId = (await r.json()).order_id;
    } catch {}
    if (newOrderId) {
      setPlacedOrder(newOrderId);
      onClear();
      toast.success("Pedido enviado! Guarde seu link de acompanhamento.");
    } else {
      toast.success("Pedido aberto no WhatsApp — é só enviar!");
    }
  };

  return (
    <>
      <AnimatePresence>
        {count > 0 && !open && (
          <motion.div
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 90, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="fixed inset-x-4 z-40 bottom-[max(1.25rem,env(safe-area-inset-bottom))]"
            data-testid="cart-dock"
          >
            <button
              type="button"
              data-testid="cart-open-button"
              onClick={() => setOpen(true)}
              className="mx-auto flex w-full max-w-xl items-center justify-between gap-4 rounded-full border border-emerald-300/40 bg-[#2D0B5A]/90 px-6 py-4 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7)] backdrop-blur-xl transition-transform duration-300 hover:scale-[1.02]"
            >
              <span className="flex items-center gap-3 text-sm font-semibold text-purple-100">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-400 text-[#1E053B]">
                  <ShoppingBag className="h-4 w-4" strokeWidth={2.5} />
                </span>
                {count} {count === 1 ? "item" : "itens"} no pedido
              </span>
              <span className="font-syne text-lg font-extrabold text-emerald-400">
                {formatBRL(total)}
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={() => setOpen(false)}
            data-testid="cart-modal-backdrop"
          >
            <motion.div
              initial={{ y: 80, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 80, opacity: 0, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[88vh] w-full overflow-y-auto rounded-t-[2rem] border border-purple-500/25 bg-[#2D0B5A] p-6 sm:max-w-lg sm:rounded-[2rem] sm:p-8"
              data-testid="cart-modal"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-syne text-2xl font-extrabold text-white">Seu pedido</h3>
                <button
                  type="button"
                  data-testid="cart-close-button"
                  onClick={() => {
                    setPlacedOrder(null);
                    setOpen(false);
                  }}
                  aria-label="Fechar pedido"
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-purple-500/30 text-purple-200 transition-colors hover:border-red-400 hover:text-red-400"
                >
                  <X className="h-4.5 w-4.5" />
                </button>
              </div>

              {placedOrder ? (
                <OrderConfirmation
                  orderId={placedOrder}
                  onClose={() => {
                    setPlacedOrder(null);
                    setOpen(false);
                  }}
                />
              ) : cart.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-purple-200/70">Seu pedido está vazio.</p>
                  <button
                    type="button"
                    data-testid="cart-empty-cta"
                    onClick={() => {
                      setOpen(false);
                      onNavigate("#monte");
                    }}
                    className="mt-5 rounded-full bg-emerald-400 px-7 py-3 font-grotesk text-xs font-bold uppercase tracking-[0.15em] text-[#1E053B]"
                  >
                    Montar meu copo
                  </button>
                </div>
              ) : (
                <>
                  <div className="mt-6 space-y-4">
                    {cart.map((item, idx) => (
                      <div
                        key={item.uid}
                        data-testid={`cart-item-${idx}`}
                        className="rounded-2xl border border-purple-500/20 bg-white/5 p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-bold text-white">{item.title}</p>
                            {item.details && item.details.length > 0 && (
                              <p className="mt-1 text-xs leading-relaxed text-purple-300/70">
                                {item.details.join(", ")}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            data-testid={`cart-remove-${idx}`}
                            onClick={() => onRemove(item.uid)}
                            aria-label="Remover item"
                            className="text-purple-400 transition-colors hover:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              data-testid={`cart-dec-${idx}`}
                              onClick={() => onDec(item.uid)}
                              aria-label="Diminuir quantidade"
                              className="flex h-8 w-8 items-center justify-center rounded-full border border-purple-500/30 text-purple-100 transition-colors hover:border-amber-300 hover:text-amber-300"
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span
                              data-testid={`cart-qty-${idx}`}
                              className="w-5 text-center text-sm font-bold text-white"
                            >
                              {item.qty}
                            </span>
                            <button
                              type="button"
                              data-testid={`cart-inc-${idx}`}
                              onClick={() => onInc(item.uid)}
                              aria-label="Aumentar quantidade"
                              className="flex h-8 w-8 items-center justify-center rounded-full border border-purple-500/30 text-purple-100 transition-colors hover:border-emerald-400 hover:text-emerald-300"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <span className="font-syne text-base font-extrabold text-emerald-400">
                            {formatBRL(item.unitPrice * item.qty)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    data-testid="cart-clear-button"
                    onClick={onClear}
                    className="mt-4 text-xs font-semibold text-purple-300/60 underline-offset-4 transition-colors hover:text-red-400 hover:underline"
                  >
                    Limpar pedido
                  </button>

                  <div className="mt-6 space-y-5 border-t border-purple-500/20 pt-6">
                    <div>
                      <label className="mb-2 block font-grotesk text-[11px] font-bold uppercase tracking-[0.25em] text-amber-400">
                        Seu nome *
                      </label>
                      <input
                        data-testid="checkout-name-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Como te chamamos?"
                        className={inputCls}
                      />
                    </div>

                    <div>
                      <label className="mb-2 block font-grotesk text-[11px] font-bold uppercase tracking-[0.25em] text-amber-400">
                        Seu WhatsApp (com DDD) *
                      </label>
                      <input
                        data-testid="checkout-phone-input"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="(41) 99999-9999"
                        inputMode="tel"
                        maxLength={16}
                        className={inputCls}
                      />
                      <p className="mt-1.5 text-[11px] text-purple-300/50">
                        A loja te avisa nesse número quando o pedido sair pra entrega.
                      </p>
                    </div>

                    <div>
                      <label className="mb-2 block font-grotesk text-[11px] font-bold uppercase tracking-[0.25em] text-amber-400">
                        Recebimento
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { id: "entrega", label: "Entrega" },
                          { id: "retirada", label: "Retirada" },
                        ].map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            data-testid={`checkout-mode-${m.id}`}
                            onClick={() => setMode(m.id)}
                            className={`rounded-xl border py-3 text-sm font-bold transition-colors duration-200 ${
                              mode === m.id
                                ? "border-transparent bg-emerald-400 text-[#1E053B]"
                                : "border-purple-500/25 bg-white/5 text-purple-100 hover:border-purple-300/50"
                            }`}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {mode === "entrega" && (
                      <>
                        <div>
                          <label className="mb-2 block font-grotesk text-[11px] font-bold uppercase tracking-[0.25em] text-amber-400">
                            Endereço completo *
                          </label>
                          <div className="relative">
                            <input
                              data-testid="checkout-address-input"
                              value={address}
                              onChange={onAddressChange}
                              autoComplete="off"
                              placeholder="Rua, número, bairro, complemento"
                              className={inputCls}
                            />
                            {suggestions.length > 0 && (
                              <div
                                data-testid="checkout-address-suggestions"
                                className="absolute inset-x-0 top-full z-20 mt-1.5 overflow-hidden rounded-xl border border-purple-500/30 bg-[#2D0B5A] shadow-2xl"
                              >
                                {suggestions.map((s, i) => (
                                  <button
                                    key={s.place_id}
                                    type="button"
                                    data-testid={`checkout-address-suggestion-${i}`}
                                    onClick={() => pickSuggestion(s)}
                                    className="flex w-full flex-col gap-0.5 px-4 py-3 text-left transition-colors duration-150 hover:bg-white/10"
                                  >
                                    <span className="text-sm font-semibold text-white">{s.main}</span>
                                    <span className="text-xs text-purple-300/60">{s.secondary}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <p className="mt-1.5 text-[11px] text-purple-300/50">
                            Obrigatório mesmo usando a localização — o entregador precisa do
                            endereço escrito.
                          </p>
                        </div>
                        <div>
                          <label className="mb-2 block font-grotesk text-[11px] font-bold uppercase tracking-[0.25em] text-amber-400">
                            Calcular entrega *
                          </label>
                          <div className="flex flex-wrap gap-2.5">
                            <button
                              type="button"
                              data-testid="checkout-locate-button"
                              onClick={locate}
                              disabled={locating}
                              className="flex items-center gap-2 rounded-full border border-purple-500/25 bg-white/5 px-4 py-2.5 text-xs font-bold text-purple-100 transition-colors duration-200 hover:border-emerald-400 hover:text-emerald-300 disabled:opacity-50"
                            >
                              {locating ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <LocateFixed className="h-3.5 w-3.5" />
                              )}
                              Usar minha localização
                            </button>
                            <button
                              type="button"
                              data-testid="checkout-geocode-button"
                              onClick={calcByAddress}
                              disabled={locating}
                              className="flex items-center gap-2 rounded-full border border-purple-500/25 bg-white/5 px-4 py-2.5 text-xs font-bold text-purple-100 transition-colors duration-200 hover:border-emerald-400 hover:text-emerald-300 disabled:opacity-50"
                            >
                              <Search className="h-3.5 w-3.5" />
                              Calcular pelo endereço
                            </button>
                          </div>
                          {distance != null && (
                            <p
                              data-testid="checkout-distance-result"
                              className="mt-3 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-4 py-2.5 text-xs font-semibold text-emerald-300"
                            >
                              ≈ {distance.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} km
                              da loja · Taxa: {fee === 0 ? "Grátis" : formatBRL(fee)}
                            </p>
                          )}
                          {showManual ? (
                            <div className="mt-3 flex flex-wrap gap-2.5">
                              {DELIVERY_KM_OPTIONS.map((o) => (
                                <button
                                  key={o.km}
                                  type="button"
                                  data-testid={`checkout-distance-${o.km}`}
                                  onClick={() => setDistance(o.km)}
                                  className={`rounded-full border px-4 py-2.5 text-xs font-bold transition-colors duration-200 ${
                                    distance === o.km
                                      ? "border-transparent bg-emerald-400 text-[#1E053B]"
                                      : "border-purple-500/25 bg-white/5 text-purple-100 hover:border-purple-300/50"
                                  }`}
                                >
                                  {o.label}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <button
                              type="button"
                              data-testid="checkout-distance-manual-toggle"
                              onClick={() => setShowManual(true)}
                              className="mt-2.5 text-[11px] font-semibold text-purple-300/60 underline-offset-4 transition-colors hover:text-amber-300 hover:underline"
                            >
                              Não deu certo? Informe a distância manualmente
                            </button>
                          )}
                          <p className="mt-2 text-[11px] leading-relaxed text-purple-300/60">
                            R$ 5,00 até 4 km · +R$ 1,50/km após 4 km · Grátis até 2 km em
                            pedidos acima de R$ 35,00
                          </p>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="mb-2 block font-grotesk text-[11px] font-bold uppercase tracking-[0.25em] text-amber-400">
                        Pagamento
                      </label>
                      <div className="flex flex-wrap gap-2.5">
                        {PAYMENTS.map((p) => (
                          <button
                            key={p}
                            type="button"
                            data-testid={`checkout-payment-${p.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            onClick={() => setPayment(p)}
                            className={`rounded-full border px-5 py-2.5 text-xs font-bold transition-colors duration-200 ${
                              payment === p
                                ? "border-transparent bg-white text-[#1E053B]"
                                : "border-purple-500/25 bg-white/5 text-purple-100 hover:border-purple-300/50"
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block font-grotesk text-[11px] font-bold uppercase tracking-[0.25em] text-amber-400">
                        Deseja colher? *
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          data-testid="checkout-spoon-sim"
                          onClick={() => setSpoonChoice("sim")}
                          className={`rounded-xl border py-3 text-sm font-bold transition-colors duration-200 ${
                            spoonChoice === "sim"
                              ? "border-transparent bg-emerald-400 text-[#1E053B]"
                              : "border-purple-500/25 bg-white/5 text-purple-100 hover:border-purple-300/50"
                          }`}
                        >
                          Sim · R$ 0,50 cada
                        </button>
                        <button
                          type="button"
                          data-testid="checkout-spoon-nao"
                          onClick={() => setSpoonChoice("nao")}
                          className={`rounded-xl border py-3 text-sm font-bold transition-colors duration-200 ${
                            spoonChoice === "nao"
                              ? "border-transparent bg-white text-[#1E053B]"
                              : "border-purple-500/25 bg-white/5 text-purple-100 hover:border-purple-300/50"
                          }`}
                        >
                          Não, obrigado
                        </button>
                      </div>
                      {spoonChoice === "sim" && (
                        <div className="mt-3 flex items-center justify-between rounded-xl border border-purple-500/25 bg-white/5 px-4 py-3">
                          <span className="text-xs font-semibold text-purple-200/80">
                            Quantidade de colheres
                          </span>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              data-testid="checkout-spoon-dec"
                              onClick={() => setSpoonQty((q) => Math.max(1, q - 1))}
                              aria-label="Menos colheres"
                              className="flex h-8 w-8 items-center justify-center rounded-full border border-purple-500/30 text-purple-100 transition-colors hover:border-amber-300 hover:text-amber-300"
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span
                              data-testid="checkout-spoon-qty"
                              className="w-5 text-center text-sm font-bold text-white"
                            >
                              {spoonQty}
                            </span>
                            <button
                              type="button"
                              data-testid="checkout-spoon-inc"
                              onClick={() => setSpoonQty((q) => Math.min(10, q + 1))}
                              aria-label="Mais colheres"
                              className="flex h-8 w-8 items-center justify-center rounded-full border border-purple-500/30 text-purple-100 transition-colors hover:border-emerald-400 hover:text-emerald-300"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="mb-2 block font-grotesk text-[11px] font-bold uppercase tracking-[0.25em] text-amber-400">
                        Observações
                      </label>
                      <textarea
                        data-testid="checkout-notes-input"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Ex: capricha no morango, troco para R$ 50..."
                        rows={2}
                        className={`${inputCls} resize-none`}
                      />
                    </div>
                  </div>

                  <div className="mt-6 space-y-2">
                    {mode === "entrega" && distance != null && (
                      <>
                        <div className="flex items-center justify-between text-sm text-purple-200/70">
                          <span>Subtotal</span>
                          <span>{formatBRL(total)}</span>
                        </div>
                        <div
                          className="flex items-center justify-between text-sm"
                          data-testid="cart-delivery-fee"
                        >
                          <span className="text-purple-200/70">Taxa de entrega</span>
                          <span className={fee === 0 ? "font-bold text-emerald-400" : "text-purple-100"}>
                            {fee === 0 ? "Grátis" : formatBRL(fee)}
                          </span>
                        </div>
                      </>
                    )}
                    {spoonChoice === "sim" && (
                      <div
                        className="flex items-center justify-between text-sm"
                        data-testid="cart-spoons-fee"
                      >
                        <span className="text-purple-200/70">Colheres ({spoonQty})</span>
                        <span className="text-purple-100">{formatBRL(spoonsCost)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-1">
                      <span className="font-grotesk text-xs font-bold uppercase tracking-[0.25em] text-purple-300/70">
                        Total
                      </span>
                      <span
                        data-testid="cart-total"
                        className="font-syne text-3xl font-extrabold text-emerald-400"
                      >
                        {formatBRL(grandTotal)}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    data-testid="checkout-submit-button"
                    onClick={send}
                    className="mt-5 flex w-full items-center justify-center gap-2.5 rounded-full bg-emerald-400 py-4.5 font-grotesk text-sm font-bold uppercase tracking-[0.15em] text-[#1E053B] transition-transform duration-300 hover:scale-[1.02] active:scale-95"
                  >
                    <MessageCircle className="h-5 w-5" />
                    Enviar pedido no WhatsApp
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
